import {useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {createProfitCenter} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {removeIndicatorSeparator, solidSelectStyles} from '../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const profitCenterSchema = Yup.object().shape({
  center_code: Yup.string()
    .required('Profit centre code is required')
    .matches(/^[a-zA-Z0-9\-_' ]{1,25}$/, {
      message:
        'Code must be 1-25 characters and can only contain letters, numbers, hyphens and underscores',
    }),
  center_name: Yup.string()
    .required('Profit centre name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,100}$/, {
      message:
        'Name must be 1-100 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  business_unit: Yup.string().required('Business unit is required'),
  center_head: Yup.string().required('Centre head is required'),
  target_revenue: Yup.number().min(0, 'Target revenue must be 0 or greater'),
  target_profit_margin: Yup.number()
    .min(0, 'Margin must be 0 or greater')
    .max(100, 'Margin cannot exceed 100%'),
  location: Yup.string(),
  status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
  description: Yup.string(),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const BUSINESS_UNIT_OPTIONS = [
  {value: 'retail', label: 'Retail'},
  {value: 'wholesale', label: 'Wholesale'},
  {value: 'manufacturing', label: 'Manufacturing'},
  {value: 'services', label: 'Services'},
  {value: 'others', label: 'Others'},
]

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProfitCenterFormValues {
  center_code: string
  center_name: string
  business_unit: string
  center_head: string
  target_revenue: number
  target_profit_margin: number
  location: string
  status: string
  description: string
}

type OptionType = {
  value: string
  label: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateProfitCenter: React.FC<{
  setCreateProfitCenter: React.Dispatch<React.SetStateAction<boolean>>
  setShouldRefreshProfitCenters: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setCreateProfitCenter, setShouldRefreshProfitCenters}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [businessUnit, setBusinessUnit] = useState<OptionType>({
    label: 'Select Business Unit...',
    value: '',
  })

  const initialValues: ProfitCenterFormValues = {
    center_code: '',
    center_name: '',
    business_unit: '',
    center_head: '',
    target_revenue: 0,
    target_profit_margin: 0,
    location: '',
    status: 'active',
    description: '',
  }

  const formik = useFormik<ProfitCenterFormValues>({
    initialValues,
    validationSchema: profitCenterSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        profit_center_id: null,
        company_id: company?.value,
        center_code: values.center_code,
        center_name: values.center_name,
        business_unit: values.business_unit,
        center_head: values.center_head,
        target_revenue: values.target_revenue || 0,
        target_profit_margin: values.target_profit_margin || 0,
        location: values.location || '',
        status: values.status,
        description: values.description || '',
      }
      await createProfitCenter(body)
        .then((res) => {
          res?.detail && toast.success('Profit Centre has been added successfully.')
          formik.resetForm()
          setShouldRefreshProfitCenters(true)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => {
          setLoading(false)
          setCreateProfitCenter(false)
        })
    },
  })

  const handleBusinessUnitChange = (option: any) => {
    setBusinessUnit(option as OptionType)
    formik.setFieldValue('business_unit', option.value, true)
  }

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_profit_center_form'
        aria-expanded='true'
        aria-controls='kt_profit_center_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Add New Profit Centre</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_profit_center_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          {/* Left Column */}
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Centre Code */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Centre Code</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='e.g. PC-001'
                  {...formik.getFieldProps('center_code')}
                />
                {formik.touched.center_code && formik.errors.center_code && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.center_code}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Centre Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Centre Name</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Profit Centre Name'
                  {...formik.getFieldProps('center_name')}
                />
                {formik.touched.center_name && formik.errors.center_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.center_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Business Unit */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Business Unit</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={businessUnit}
                  onChange={handleBusinessUnitChange}
                  options={BUSINESS_UNIT_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Centre Head */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Centre Head</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Centre Head'
                  {...formik.getFieldProps('center_head')}
                />
                {formik.touched.center_head && formik.errors.center_head && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.center_head}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Location / Region</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Location / Region'
                  {...formik.getFieldProps('location')}
                />
                {formik.touched.location && formik.errors.location && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.location}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='p-8 col pt-0 pt-md-8 pb-md-4'>
            {/* Target Revenue */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>
                Target Revenue (&#8377;)
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Target Revenue'
                  {...formik.getFieldProps('target_revenue')}
                />
                {formik.touched.target_revenue && formik.errors.target_revenue && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.target_revenue}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Target Profit Margin */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Target Margin (%)</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Target Profit Margin %'
                  {...formik.getFieldProps('target_profit_margin')}
                />
                {formik.touched.target_profit_margin && formik.errors.target_profit_margin && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.target_profit_margin}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Status</label>
              <div className='col-lg-9 fv-row d-flex'>
                <span className='form-check form-check-custom form-check-solid fs-6'>
                  Active{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='active'
                    checked={formik.values.status === 'active'}
                    onChange={() => formik.setFieldValue('status', 'active')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
                <span
                  style={{marginLeft: '20px'}}
                  className='form-check form-check-custom form-check-solid fs-6'
                >
                  Inactive{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='inactive'
                    checked={formik.values.status === 'inactive'}
                    onChange={() => formik.setFieldValue('status', 'inactive')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
              </div>
            </div>

            {/* Description */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Description</label>
              <div className='col-lg-9 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Description'
                  rows={4}
                  {...formik.getFieldProps('description')}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setCreateProfitCenter(false)
              setShouldRefreshProfitCenters(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading ? 'Add Profit Centre' : null}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
